<template>
    <div
        ref="basePickerComponent"
        class="relative dark:border-base-300/50 bg-base-100 dark:bg-base-200"
        @click="showCalendar"
        @focusin="showCalendar"
    >
        <div
            ref="pickerField"
            class="input-sm date-picker-field flex items-center border border-base-300 px-0"
            :class="[
                'input-' + props.size,
                props.size === 'sm' ? 'h-8' : 'h-10',
                bgColorClass,
                errorBorderClass,
                yearColorHint ? 'border-r-4': ''
            ]"
            :style="{'border-right-color': yearColorHint ? borderRightColor: ''}"
        >
            <label
                v-if="label"
                :for="dateId"
                class="absolute top-[-8px] left-[5px] text-xs w-fit z-[9999]"
                :class="[isDisabled ? 'text-base-300':'']"
            >
                {{ label }}
            </label>
            <input
                :id="dateId"
                ref="textInputRef"
                :placeholder="placeholder"
                :value="dateStr"
                autocomplete="off"
                class="pl-3 w-full bg-transparent border-none rounded-none outline-none"
                data-cy="date-picker-input-value"
                :disabled="isDisabled"
                :class="isDisabled ? 'text-base-300' : ''"
                @keydown.esc="hideCalendar"
                @keydown.ctrl.shift.right="hideCalendar"
                @keydown.ctrl.shift.up="hideCalendar"
                @keydown.ctrl.shift.down="hideCalendar"
                @keydown.tab="handleOnSubmit"
                @keydown.enter="handleOnSubmit"
                @keydown.page-up.prevent="props.pageUp"
                @keydown.page-down.prevent="props.pageDown"
                @keydown.down.exact="props.focusPicker"
            >
        </div>
        <div v-if="calendarVisible" ref="calendarContainer" class="absolute top-[8px] z-10">
            <slot :date-picker-pos="datePickerPos"/>
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, PropType, ref, watch} from 'vue'
import {HTMLInputElement} from 'happy-dom'
import {LINE_COLUMN} from '@/transaction/create-transaction/EntryLineInstance'
import {todayYear} from '@/dateUtils/dateUtils'
import {flip, useFloating} from '@floating-ui/vue'

const props = defineProps({
    dateId: String,
    size: {
        type: String,
        default: 'md'
    },
    label: String,
    placeholder: String,
    hideCalenderIcon: {
        type: Boolean,
        default: false
    },
    isError: {
        type: Boolean,
        default: false
    },
    isDisabled: Boolean,
    dateFormat: {
        type: String,
        default: undefined
    },
    dataColId: {
        type: String as PropType<LINE_COLUMN>,
        default: undefined
    },
    handleOnSubmit: {
        type: Function as PropType<(event: KeyboardEvent) => void>,
        required: true
    },
    focusPicker: {
        type: Function as PropType<() => void>,
        required: true
    },
    year: {
        type: String,
        required: false,
    },
    dateStr: {
        type: String,
        required: false,
    },
    pageUp: {
        type: Function,
        required: true
    },
    pageDown: {
        type: Function,
        required: true
    },
    yearColorHint: {
        type: Boolean,
        required: true
    },
    disableBackgroundColorClass: {
        type: Boolean,
        required: false
    }
})

const pickerField = ref<HTMLInputElement | null>(null)
const calendarContainer = ref<HTMLElement | null>(null)
const basePickerComponent = ref<HTMLElement | null>(null)

const {floatingStyles} = useFloating(basePickerComponent, calendarContainer, {
    placement: 'bottom-start',
    transform: false,
    middleware: [
        flip()
    ]
})

const textInputRef = ref<HTMLInputElement>()

const calendarVisible = ref(false)
const isCurrentYear = computed(() => {
    if (!props.year) return
    return props.year === todayYear
})

const datePickerPos = ref({top: '', left: '', right: ''})
watch(floatingStyles, (floatingStyle) => {
    datePickerPos.value = {
        ...datePickerPos.value,
        top: floatingStyle.top,
        left: floatingStyle.left,
    }
})

const invokeFocus = () => {
    textInputRef.value?.focus()
}

const selectText = () => {
    textInputRef.value?.select()
}

const showCalendar = () => {
    if (props.isDisabled || calendarVisible.value) return
    calendarVisible.value = true
    selectText()
}

const hideCalendar = () => {
    calendarVisible.value = false
}

const borderRightColor = computed(() => {
    if (isCurrentYear.value === undefined) return
    if (props.isError) return '#ef4444'
    return isCurrentYear.value ? '#9090ff' : '#ffb465'
})

const bgColorClass = computed(() => {
    if (props.disableBackgroundColorClass) return 'bg-transparent'
    return isCurrentYear.value ? 'bg-info/20 dark:bg-transparent' : 'bg-warning/20 dark:bg-transparent'
})

const errorBorderClass = computed(() => {
    return props.isError ? 'border-red-500' : ''
})

defineExpose({
    focus: invokeFocus,
    hide: hideCalendar,
})

</script>

<style>
@reference "@/index.css";
@reference "tailwindcss";

.date-picker-field {

    //Assuming only gets joined as right item with no further items to the right

    .join-item > & {
        @apply rounded-l-none;
    }
}

.date-picker-label {

    i {
        @apply w-10 h-10;
    }
}

.date-picker-input {
    @apply join-item input-sm bg-transparent w-40 outline-none;
}
</style>